/**
 * PORTED — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/settings-client.tsx`.
 *
 * Portable client (ops already mounted — §3 of shared-render-ownership.md).
 * Co-ported as a private delegate of the `_crm/*View` family so the view
 * rewrites can render. Changes, and only these:
 *   `"use client"`                     -> dropped
 *   `useRouter` / `useSearchParams`    -> `useNavigate` / `useSearch` from the router seam
 *   `next/link`                        -> `RouterLink` (if present)
 *   `router.push/back/refresh`         -> `navigate.*`
 */


/**
 * Client-side settings editors for CRM, re-homed inside the `sales` project
 * section. These are location-agnostic (all mutations go through `apiCall` +
 * `navigate.refresh()` — no `/crm` navigation), so they're lifted verbatim from
 * the now-deprecated `products/crm/app/settings/*` clients. Composed by
 * `CrmSettingsView` in `views.tsx`, surfaced via the section's Settings link.
 */

import { useState, useTransition, type ReactNode } from "react";
import { useNavigate } from "@excellent/ui/router";
import {
  Badge,
  Button,
  FormField,
  Input,
  PipelineEditor,
  ReferenceListEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  TemplateEditor,
  Textarea,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import {
  pipelines as crmPipelines,
  settings as crmSettings,
  templates as crmTemplates,
  smart_views as crmSmartViews,
  sequences as crmSequences,
  seed as seedContract,
} from "@excellent/ui/api/contracts/crm";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

// ------------------------------------------------------------ Pipelines

interface CrmStage {
  id: number;
  workspace_id: number;
  pipeline_id: number;
  slug: string;
  name: string;
  description: string | null;
  sort_key: string;
  probability_pct: number;
  is_won: number;
  is_lost: number;
}

interface CrmPipeline {
  id: number;
  workspace_id: number;
  slug: string;
  name: string;
  description: string | null;
  is_default: number;
  sort_key: string;
  stages: CrmStage[];
}

function stageTone(stage: CrmStage): string {
  if (stage.is_won === 1) return "won";
  if (stage.is_lost === 1) return "lost";
  return `${stage.probability_pct}%`;
}

export function CrmPipelinesEditorClient({
  pipelines,
  description,
}: {
  pipelines: CrmPipeline[];
  /** Forwarded to the editor's SettingsSection header (e.g. the sales-motion hint). */
  description?: ReactNode;
}) {
  const adapted = pipelines.map((p) => ({
    ...p,
    stages: [...p.stages]
      .sort((a, b) => a.sort_key.localeCompare(b.sort_key))
      .map((s, idx) => ({ ...s, position: idx + 1 })),
  }));

  return (
    <PipelineEditor
      pipelines={adapted}
      description={description}
      newStageDefaults={{ probability_pct: 50, is_won: false, is_lost: false }}
      renderStageBadge={(stage) => (
        <Badge variant="outline">{stageTone(stage as unknown as CrmStage)}</Badge>
      )}
      renderNewStageExtras={(extras, setExtras) => (
        <Input
          type="number"
          min={0}
          max={100}
          placeholder="probability %"
          value={(extras.probability_pct as number | undefined) ?? 50}
          onChange={(e) =>
            setExtras({ probability_pct: Number(e.target.value) || 0 })
          }
          className="w-24"
        />
      )}
      onPipelineCreate={async ({ slug, name }) => {
        await apiCall(crmPipelines.create, { body: { slug, name } });
      }}
      onPipelineSetDefault={async (id) => {
        await apiCall(crmPipelines.set_default, { params: { id } });
      }}
      onStageCreate={async (pipelineId, draft) => {
        await apiCall(crmPipelines.stage_create, {
          params: { id: pipelineId },
          body: {
            slug: draft.slug,
            name: draft.name,
            probability_pct:
              (draft.extras.probability_pct as number | undefined) ?? 50,
          },
        });
      }}
      onStageUpdate={async (pipelineId, stageId, patch) => {
        await apiCall(crmPipelines.stage_update, {
          params: { id: pipelineId, stageId },
          body: patch,
        });
      }}
      onStageArchive={async (pipelineId, stageId) => {
        await apiCall(crmPipelines.stage_archive, {
          params: { id: pipelineId, stageId },
        });
      }}
    />
  );
}

// ------------------------------------------------------------ Lead statuses

interface StatusRow {
  id: number;
  workspace_id: number;
  slug: string;
  name: string;
  color: string | null;
  sort_key: string;
  is_terminal: number;
}

export function CrmStatusesEditorClient({
  statuses,
}: {
  statuses: StatusRow[];
}) {
  return (
    <ReferenceListEditor
      title="Lead statuses"
      rows={statuses}
      flags={[{ key: "is_terminal", label: "terminal" }]}
      itemNoun="status"
      onCreate={async ({ slug, name, flags }) => {
        await apiCall(crmSettings.status_create, {
          body: { slug, name, is_terminal: flags.is_terminal ?? false },
        });
      }}
      onUpdate={async (id, patch) => {
        await apiCall(crmSettings.status_update, {
          params: { id },
          body: {
            name: patch.name,
            is_terminal: patch.flags?.is_terminal,
          },
        });
      }}
      onArchive={async (id) => {
        await apiCall(crmSettings.status_archive, { params: { id } });
      }}
    />
  );
}

// ------------------------------------------------------------ Lead sources

interface SourceRow {
  id: number;
  workspace_id: number;
  slug: string;
  name: string;
}

export function CrmSourcesEditorClient({ sources }: { sources: SourceRow[] }) {
  return (
    <ReferenceListEditor
      title="Lead sources"
      rows={sources}
      itemNoun="source"
      onCreate={async ({ slug, name }) => {
        await apiCall(crmSettings.source_create, { body: { slug, name } });
      }}
      onUpdate={async (id, patch) => {
        if (patch.name !== undefined) {
          await apiCall(crmSettings.source_update, {
            params: { id },
            body: { name: patch.name },
          });
        }
      }}
      onArchive={async (id) => {
        await apiCall(crmSettings.source_archive, { params: { id } });
      }}
    />
  );
}

// ------------------------------------------------------------ Email templates

interface TemplateView {
  id: number;
  slug: string;
  name: string;
  subject: string;
  body: string;
}

export function CrmTemplatesEditorClient({
  templates,
  title,
  description,
}: {
  templates: TemplateView[];
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <TemplateEditor
      title={title}
      description={description}
      templates={templates.map((t) => ({
        id: t.id,
        name: t.name,
        subject: t.subject,
        body: t.body,
      }))}
      itemNoun="template"
      onCreate={async ({ name, subject, body }) => {
        await apiCall(crmTemplates.create, {
          body: {
            slug: slugify(name) || `t-${Date.now()}`,
            name,
            subject,
            body_text: body,
          },
        });
      }}
      onUpdate={async (id, patch) => {
        await apiCall(crmTemplates.update, {
          params: { id },
          body: {
            name: patch.name,
            subject: patch.subject,
            body_text: patch.body,
          },
        });
      }}
      onArchive={async (id) => {
        await apiCall(crmTemplates.archive, { params: { id } });
      }}
    />
  );
}

// ------------------------------------------------------------ Smart views

interface SmartViewRow {
  id: number;
  slug: string;
  name: string;
  entity_kind: "crm_lead" | "crm_opportunity";
  shared: number;
  query_json: string;
}

function parseQuery(raw: string): unknown {
  if (raw.trim() === "") return {};
  return JSON.parse(raw);
}

export function CrmSmartViewsEditorClient({
  views,
}: {
  views: SmartViewRow[];
}) {
  const navigate = useNavigate();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    entity_kind: "crm_lead" | "crm_opportunity";
    query: string;
  }>({
    name: "",
    entity_kind: "crm_lead",
    query: "{}",
  });

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        navigate.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  return (
    <SettingsSection title="Smart views">
      <div className="flex flex-col gap-3">
        {views.length === 0 ? (
          <div className="type-body text-muted-foreground">
            No smart views yet. Add one below.
          </div>
        ) : (
          <ul className="flex flex-col">
            {views.map((v) => {
              const open = openId === v.id;
              return (
                <li
                  key={v.id}
                  className="flex flex-col gap-2 border-b border-border/40 py-3 first:pt-0 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : v.id)}
                      className="flex flex-1 items-center gap-2 text-left"
                    >
                      <span className="type-body text-foreground">{v.name}</span>
                      <Badge variant="outline">
                        {v.entity_kind === "crm_lead" ? "leads" : "opportunities"}
                      </Badge>
                      {v.shared === 1 ? <Badge variant="outline">shared</Badge> : null}
                    </button>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={pending}
                      onClick={() =>
                        run(() =>
                          apiCall(crmSmartViews.delete, { params: { id: v.id } }),
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                  {open ? (
                    <div className="space-y-2 rounded-md bg-surface-2 p-3">
                        <Input
                          defaultValue={v.name}
                          onBlur={(e) => {
                            if (e.target.value !== v.name) {
                              run(() =>
                                apiCall(crmSmartViews.update, {
                                  params: { id: v.id },
                                  body: { name: e.target.value },
                                }),
                              );
                            }
                          }}
                        />
                        <Textarea
                          rows={6}
                          defaultValue={JSON.stringify(
                            JSON.parse(v.query_json),
                            null,
                            2,
                          )}
                          onBlur={(e) => {
                            try {
                              const parsed = parseQuery(e.target.value);
                              run(() =>
                                apiCall(crmSmartViews.update, {
                                  params: { id: v.id },
                                  body: {
                                    query: parsed as Record<string, unknown>,
                                  },
                                }),
                              );
                            } catch (err) {
                              setError(
                                err instanceof Error
                                  ? `Invalid JSON: ${err.message}`
                                  : "Invalid JSON",
                              );
                            }
                          }}
                          className="font-mono type-subtext"
                        />
                        <p className="type-subtext text-muted-foreground">
                          See SmartViewQuery schema for valid fields (status_slugs,
                          source_slugs, owner_actor_id, last_activity_after, …).
                        </p>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Input
              placeholder="New smart view name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <Select
              value={draft.entity_kind}
              onValueChange={(v) =>
                setDraft({
                  ...draft,
                  entity_kind: v as "crm_lead" | "crm_opportunity",
                })
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="crm_lead">Leads</SelectItem>
                <SelectItem value="crm_opportunity">Opportunities</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea
            placeholder='Query JSON, e.g. {"status_slugs":["qualified"]}'
            rows={4}
            value={draft.query}
            onChange={(e) => setDraft({ ...draft, query: e.target.value })}
            className="font-mono type-subtext"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={pending || !draft.name}
              onClick={() => {
                let query: Record<string, unknown>;
                try {
                  query = (parseQuery(draft.query) as Record<string, unknown>) ?? {};
                } catch (err) {
                  setError(
                    err instanceof Error
                      ? `Invalid JSON: ${err.message}`
                      : "Invalid JSON",
                  );
                  return;
                }
                const snapshot = { ...draft, query };
                run(async () => {
                  await apiCall(crmSmartViews.create, {
                    body: {
                      slug: slugify(snapshot.name) || `sv-${Date.now()}`,
                      name: snapshot.name,
                      entity_kind: snapshot.entity_kind,
                      query: snapshot.query,
                    },
                  });
                  setDraft({ name: "", entity_kind: "crm_lead", query: "{}" });
                });
              }}
            >
              Add smart view
            </Button>
          </div>
        </div>

        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </div>
    </SettingsSection>
  );
}

// ------------------------------------------------------------ Sequences

type Channel = "email" | "sms" | "call" | "task";

interface Step {
  id: number;
  sort_key: string;
  day_offset: number;
  channel: Channel;
  template_id: number | null;
  instructions: string | null;
}

interface SequenceRow {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  paused: number;
  steps: Step[];
}

interface TemplateOption {
  id: number;
  name: string;
}

const CHANNELS: Channel[] = ["email", "sms", "call", "task"];

export function CrmSequencesEditorClient({
  sequences,
  templates,
}: {
  sequences: SequenceRow[];
  templates: TemplateOption[];
}) {
  const navigate = useNavigate();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [newSeq, setNewSeq] = useState({ name: "", description: "" });
  const [stepDrafts, setStepDrafts] = useState<
    Record<
      number,
      {
        day_offset: string;
        channel: Channel;
        template_id: string;
        instructions: string;
      }
    >
  >({});

  function getStepDraft(seqId: number) {
    return (
      stepDrafts[seqId] ?? {
        day_offset: "0",
        channel: "email" as Channel,
        template_id: "",
        instructions: "",
      }
    );
  }

  function setStepDraft(
    seqId: number,
    patch: Partial<{
      day_offset: string;
      channel: Channel;
      template_id: string;
      instructions: string;
    }>,
  ) {
    setStepDrafts((prev) => ({
      ...prev,
      [seqId]: { ...getStepDraft(seqId), ...patch },
    }));
  }

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        navigate.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  return (
    <SettingsSection title="Sequences">
      <div className="flex flex-col">
        {sequences.map((s) => {
          const open = openId === s.id;
          const draft = getStepDraft(s.id);
          return (
            <div
              key={s.id}
              className="space-y-2 border-b border-border/40 py-4 first:pt-0"
            >
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : s.id)}
                    className="flex flex-1 items-baseline gap-2 text-left"
                  >
                    <span className="type-body text-foreground">{s.name}</span>
                    <span className="type-body text-muted-foreground">
                      /{s.slug} · {s.steps.length} step
                      {s.steps.length === 1 ? "" : "s"}
                    </span>
                    {s.paused === 1 ? (
                      <Badge variant="outline">paused</Badge>
                    ) : null}
                  </button>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={pending}
                    onClick={() =>
                      run(() =>
                        apiCall(crmSequences.update, {
                          params: { id: s.id },
                          body: { paused: s.paused !== 1 },
                        }),
                      )
                    }
                  >
                    {s.paused === 1 ? "Resume" : "Pause"}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={pending}
                    onClick={() =>
                      run(() =>
                        apiCall(crmSequences.archive, { params: { id: s.id } }),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>

                {open ? (
                  <div className="space-y-3">
                    <Input
                      defaultValue={s.name}
                      placeholder="Sequence name"
                      onBlur={(e) => {
                        if (e.target.value !== s.name) {
                          run(() =>
                            apiCall(crmSequences.update, {
                              params: { id: s.id },
                              body: { name: e.target.value },
                            }),
                          );
                        }
                      }}
                    />
                    <Input
                      defaultValue={s.description ?? ""}
                      placeholder="Description"
                      onBlur={(e) => {
                        if (e.target.value !== (s.description ?? "")) {
                          run(() =>
                            apiCall(crmSequences.update, {
                              params: { id: s.id },
                              body: { description: e.target.value || null },
                            }),
                          );
                        }
                      }}
                    />

                    {s.steps.length === 0 ? (
                      <div className="type-body text-muted-foreground">
                        No steps yet.
                      </div>
                    ) : (
                      <ul className="flex flex-col">
                        {s.steps.map((st, idx) => (
                          <li
                            key={st.id}
                            className={
                              idx === s.steps.length - 1
                                ? "flex items-center justify-between gap-2 py-2 first:pt-0"
                                : "flex items-center justify-between gap-2 border-b border-border/40 py-2 first:pt-0"
                            }
                          >
                            <div className="flex min-w-0 items-center gap-2 type-body">
                              <Badge variant="outline">
                                day +{st.day_offset}
                              </Badge>
                              <Badge variant="outline" className="capitalize">
                                {st.channel}
                              </Badge>
                              {st.template_id ? (
                                <span className="text-muted-foreground">
                                  template #{st.template_id}
                                </span>
                              ) : null}
                              {st.instructions ? (
                                <span className="truncate text-muted-foreground">
                                  · {st.instructions}
                                </span>
                              ) : null}
                            </div>
                            <Button
                              size="xs"
                              variant="ghost"
                              disabled={pending}
                              onClick={() =>
                                run(() =>
                                  apiCall(crmSequences.step_archive, {
                                    params: { id: s.id, stepId: st.id },
                                  }),
                                )
                              }
                            >
                              Remove
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="grid grid-cols-[80px_1fr_1fr_auto] gap-2 items-end">
                      <Input
                        type="number"
                        placeholder="day"
                        min={0}
                        value={draft.day_offset}
                        onChange={(e) =>
                          setStepDraft(s.id, { day_offset: e.target.value })
                        }
                      />
                      <Select
                        value={draft.channel}
                        onValueChange={(v) =>
                          setStepDraft(s.id, { channel: v as Channel })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHANNELS.map((c) => (
                            <SelectItem key={c} value={c}>
                              <span className="capitalize">{c}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={draft.template_id || "none"}
                        onValueChange={(v) =>
                          setStepDraft(s.id, {
                            template_id: v === "none" ? "" : v,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Template (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No template</SelectItem>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        disabled={pending}
                        onClick={() => {
                          run(async () => {
                            await apiCall(crmSequences.step_create, {
                              params: { id: s.id },
                              body: {
                                day_offset: Number(draft.day_offset) || 0,
                                channel: draft.channel,
                                template_id: draft.template_id
                                  ? Number(draft.template_id)
                                  : null,
                                instructions: draft.instructions || null,
                              },
                            });
                            setStepDraft(s.id, {
                              day_offset: "0",
                              channel: "email",
                              template_id: "",
                              instructions: "",
                            });
                          });
                        }}
                      >
                        Add step
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Instructions (optional)"
                      rows={2}
                      value={draft.instructions}
                      onChange={(e) =>
                        setStepDraft(s.id, { instructions: e.target.value })
                      }
                    />
                  </div>
                ) : null}
            </div>
          );
        })}

        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end pt-4">
          <Input
            placeholder="New sequence name"
            value={newSeq.name}
            onChange={(e) => setNewSeq({ ...newSeq, name: e.target.value })}
          />
          <Input
            placeholder="Description (optional)"
            value={newSeq.description}
            onChange={(e) =>
              setNewSeq({ ...newSeq, description: e.target.value })
            }
          />
          <Button
            size="sm"
            disabled={pending || !newSeq.name}
            onClick={() => {
              const snapshot = newSeq;
              run(async () => {
                await apiCall(crmSequences.create, {
                  body: {
                    slug: slugify(snapshot.name) || `seq-${Date.now()}`,
                    name: snapshot.name,
                    description: snapshot.description || null,
                  },
                });
                setNewSeq({ name: "", description: "" });
              });
            }}
          >
            Add sequence
          </Button>
        </div>

        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </div>
    </SettingsSection>
  );
}

// ------------------------------------------------------------ Seed

export function SeedApplyClient() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    already_applied: boolean;
    seed_version: number;
  } | null>(null);

  async function apply() {
    setBusy(true);
    try {
      const r = await apiCall(seedContract.apply, { body: { version: 1 } });
      setResult(r);
      navigate.refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSection
      title="Seed data"
    >
      <div className="flex items-center gap-3 type-body">
        <p className="flex-1 text-muted-foreground">
          Applies v1: default pipeline + 6 stages + 4 statuses + 6 sources + 2
          templates + 4 smart views + a 3-step starter sequence. It does not
          create organizations, people, leads, opportunities, or activity.
          Idempotent: re-running is a no-op.
        </p>
        <Button onClick={apply} disabled={busy}>
          {busy ? "Applying…" : "Apply seed"}
        </Button>
        {result && (
          <span className="text-muted-foreground">
            {result.already_applied
              ? "Already applied"
              : `Applied v${result.seed_version}`}
          </span>
        )}
      </div>
    </SettingsSection>
  );
}

// ------------------------------------------------------------ Email sending

interface EmailTransportConfigView {
  provider?: "log" | "resend" | "http";
  from?: string;
  endpoint?: string;
}

/**
 * The log→send switch. Picks the transport (log / Resend / generic HTTP) and the
 * non-secret config. The API key is set via the `CRM_EMAIL_API_KEY` env var, NOT
 * here — `keyFromEnv` reports whether that variable is present.
 */
export function CrmEmailTransportEditorClient({
  initial,
  keyFromEnv,
}: {
  initial: EmailTransportConfigView;
  keyFromEnv: boolean;
}) {
  const navigate = useNavigate();
  const [provider, setProvider] = useState<"log" | "resend" | "http">(
    initial.provider ?? "log",
  );
  const [from, setFrom] = useState(initial.from ?? "");
  const [endpoint, setEndpoint] = useState(initial.endpoint ?? "");
  const [busy, start] = useTransition();
  const [saved, setSaved] = useState(false);

  function save() {
    start(async () => {
      await apiCall(crmSettings.email_set, {
        body: {
          provider,
          from: from.trim() || undefined,
          endpoint: endpoint.trim() || undefined,
        },
      });
      setSaved(true);
      navigate.refresh();
    });
  }

  const needsKey = provider !== "log";

  return (
    <SettingsSection
      title="Email sending"
    >
      <div className="flex flex-col gap-4">
        <FormField label="Transport" muted>
            <Select
              value={provider}
              onValueChange={(v) => {
                setProvider(v as "log" | "resend" | "http");
                setSaved(false);
              }}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="log">Log only (record, don’t send)</SelectItem>
                <SelectItem value="resend">Resend</SelectItem>
                <SelectItem value="http">Generic HTTP endpoint</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          {needsKey && (
            <FormField label="From address" muted>
              <Input
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setSaved(false);
                }}
                placeholder="sales@yourdomain.com"
                className="w-80"
              />
            </FormField>
          )}

          {provider === "http" && (
            <FormField label="Endpoint URL" muted>
              <Input
                value={endpoint}
                onChange={(e) => {
                  setEndpoint(e.target.value);
                  setSaved(false);
                }}
                placeholder="https://mail.internal.example/send"
                className="w-80"
              />
            </FormField>
          )}

          {needsKey && (
            <p className="text-muted-foreground">
              {keyFromEnv
                ? "API key detected (CRM_EMAIL_API_KEY). Sends are live."
                : "Set the CRM_EMAIL_API_KEY environment variable to authorize sending — the key is never stored in the database."}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
            {saved && <span className="text-muted-foreground">Saved</span>}
          </div>
      </div>
    </SettingsSection>
  );
}

// ------------------------------------------------------------ Forecast target

/**
 * The quarter bookings target — the quota `/customers?view=forecast` measures
 * projected close against. Stored in minor units; edited here in dollars. An
 * empty value clears it (the forecast degrades to a target-less projection).
 */
export function CrmForecastTargetEditorClient({
  initialMinor,
}: {
  initialMinor: number | null;
}) {
  const navigate = useNavigate();
  const [dollars, setDollars] = useState(
    initialMinor != null ? String(Math.round(initialMinor / 100)) : "",
  );
  const [busy, start] = useTransition();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function save() {
    const trimmed = dollars.trim();
    let minor: number | null = null;
    if (trimmed !== "") {
      const n = Number(trimmed.replace(/[$,\s]/g, ""));
      if (!Number.isFinite(n) || n < 0) {
        setError("Enter a dollar amount, e.g. 250000.");
        return;
      }
      minor = Math.round(n * 100);
    }
    setError(null);
    start(async () => {
      await apiCall(crmSettings.forecast_target_set, { body: { minor } });
      setSaved(true);
      navigate.refresh();
    });
  }

  return (
    <SettingsSection
      title="Quarter bookings target"
    >
      <div className="flex flex-col gap-4">
        <FormField label="Target (USD this quarter)" muted>
          <Input
            value={dollars}
            onChange={(e) => {
              setDollars(e.target.value);
              setSaved(false);
            }}
            placeholder="250000"
            inputMode="numeric"
            className="w-64"
          />
        </FormField>
        {error && <p className="text-danger">{error}</p>}
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {saved && <span className="text-muted-foreground">Saved</span>}
        </div>
      </div>
    </SettingsSection>
  );
}

// ------------------------------------------------------------ SMS sending

interface SmsTransportConfigView {
  provider?: "log" | "twilio" | "http";
  from?: string;
  accountSid?: string;
  endpoint?: string;
}

/**
 * The SMS analog of the email transport switch. Auth token lives in the
 * CRM_SMS_AUTH_TOKEN / CRM_SMS_API_KEY env var, never here.
 */
export function CrmSmsTransportEditorClient({
  initial,
  keyFromEnv,
}: {
  initial: SmsTransportConfigView;
  keyFromEnv: boolean;
}) {
  const navigate = useNavigate();
  const [provider, setProvider] = useState<"log" | "twilio" | "http">(
    initial.provider ?? "log",
  );
  const [from, setFrom] = useState(initial.from ?? "");
  const [accountSid, setAccountSid] = useState(initial.accountSid ?? "");
  const [endpoint, setEndpoint] = useState(initial.endpoint ?? "");
  const [busy, start] = useTransition();
  const [saved, setSaved] = useState(false);

  function save() {
    start(async () => {
      await apiCall(crmSettings.sms_set, {
        body: {
          provider,
          from: from.trim() || undefined,
          accountSid: accountSid.trim() || undefined,
          endpoint: endpoint.trim() || undefined,
        },
      });
      setSaved(true);
      navigate.refresh();
    });
  }

  const needsKey = provider !== "log";

  return (
    <SettingsSection
      title="SMS sending"
    >
      <div className="flex flex-col gap-4">
        <FormField label="Transport" muted>
            <Select
              value={provider}
              onValueChange={(v) => {
                setProvider(v as "log" | "twilio" | "http");
                setSaved(false);
              }}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="log">Log only (record, don’t send)</SelectItem>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="http">Generic HTTP endpoint</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          {needsKey && (
            <FormField label="From number" muted>
              <Input
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setSaved(false);
                }}
                placeholder="+15551234567"
                className="w-80"
              />
            </FormField>
          )}

          {provider === "twilio" && (
            <FormField label="Twilio Account SID" muted>
              <Input
                value={accountSid}
                onChange={(e) => {
                  setAccountSid(e.target.value);
                  setSaved(false);
                }}
                placeholder="ACxxxxxxxx"
                className="w-80"
              />
            </FormField>
          )}

          {provider === "http" && (
            <FormField label="Endpoint URL" muted>
              <Input
                value={endpoint}
                onChange={(e) => {
                  setEndpoint(e.target.value);
                  setSaved(false);
                }}
                placeholder="https://sms.internal.example/send"
                className="w-80"
              />
            </FormField>
          )}

          {needsKey && (
            <p className="text-muted-foreground">
              {keyFromEnv
                ? "Auth token detected. Sends are live."
                : provider === "twilio"
                  ? "Set the CRM_SMS_AUTH_TOKEN environment variable (your Twilio auth token) to authorize sending — never stored in the database."
                  : "Set the CRM_SMS_API_KEY environment variable to authorize sending — never stored in the database."}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
            {saved && <span className="text-muted-foreground">Saved</span>}
          </div>
      </div>
    </SettingsSection>
  );
}
